import { LIMITS_HUMAN } from '../core/limits';
import type { UiMessages } from './types';

export const uiEn: UiMessages = {
  lang: 'en',
  brand: 'PDF & Print',

  nav: {
    home: 'Home',
    tools: {
      merge: 'Merge PDFs',
      extract: 'Extract pages',
      organize: 'Organize pages',
      rotate: 'Rotate pages',
    },
    printForms: 'Create printable forms',
    about: 'About',
    privacy: 'Privacy',
    contact: 'Contact',
    switchLang: '한국어',
    switchLangAria: 'Open the same tool in Korean',
    skipToEditor: 'Skip to the editor',
  },

  editor: {
    dropTitle: 'Drop PDF files here',
    dropHintMulti: 'You can pick several at once. Files are opened inside this browser only.',
    dropHintSingle: 'Pick one PDF. The file is opened inside this browser only.',
    chooseFiles: 'Choose PDF files',
    chooseFile: 'Choose a PDF file',
    addMore: 'Add more files',
    replaceFile: 'Use a different file',
    reset: 'Start over',
    retry: 'Try again',
    download: 'Download the new PDF',
    working: 'Working…',
    noScript:
      'This tool needs JavaScript because it processes the PDF in your browser rather than on a server. The instructions and FAQ below are readable as they are.',

    filesTitle: 'Loaded files',
    moveFileUp: 'Move this file earlier',
    moveFileDown: 'Move this file later',
    removeFile: 'Remove this file',

    pagesTitle: 'Pages',
    gridAria: 'Page list. Move with the arrow keys, select with space.',
    emptyGrid: 'No pages loaded yet.',
    selectAll: 'Select all',
    clearSelection: 'Clear selection',
    deleteSelected: 'Delete selected pages',
    rotateLeft: 'Rotate 90° left',
    rotateRight: 'Rotate 90° right',
    resetRotation: 'Undo rotation',
    moveLeft: 'Move earlier',
    moveRight: 'Move later',
    moveFirst: 'Move to front',
    moveLast: 'Move to end',
    removePage: 'Delete this page',
    keyboardHint:
      'Keyboard: arrow keys move between pages, space or Enter selects, Ctrl+←/→ reorders, Ctrl+Home/End sends a page to the front or the end.',

    rangeLabel: 'Pages to extract',
    rangePlaceholder: 'e.g. 1,3-5',
    rangeHelp:
      'Page numbers start at 1 and are separated by commas. Pages come out in the order you type them, and repeating a number is reported as an error.',
    rangeClear: 'Clear the box',
    rangeAll: 'All pages',

    thumbnailPending: 'Preparing preview',
    thumbnailFailed: 'Could not render this preview',

    preflightLimits: `Files are processed in this browser and not sent to a server. Up to ${LIMITS_HUMAN.maxFileMiB} MiB per file · ${LIMITS_HUMAN.maxTotalMiB} MiB total · ${LIMITS_HUMAN.maxTotalPages} pages · ${LIMITS_HUMAN.maxFiles} files.`,
    cautionSummary: 'Digital signatures, form fields and bookmarks may not survive into the new file.',
    cautionMore: 'See the full limitations',
    cautionBody:
      'This tool works on the pages of ordinary PDFs. Digital signatures, form (AcroForm) values, bookmarks and tagged-PDF structure may not survive into the new file. Keep your own copy of signed or form-based documents. Password-protected PDFs cannot be opened here, and there is no password removal. Originals are not written to browser storage, and they leave memory when you close the tab.',
    deviceNote: 'Speed and what actually fits depend on your device and browser.',
  },

  fmt: {
    pageCount: (n) => (n === 1 ? '1 page' : `${n} pages`),
    fileCount: (n) => (n === 1 ? '1 file' : `${n} files`),
    selectedCount: (n) => (n === 0 ? 'Nothing selected' : `${n} selected`),
    pageBadge: (n) => `${n}`,
    pageRef: (n) => `page ${n}`,
    rotationBadge: (degrees) => `${degrees}°`,
    sizeBadge: (width, height) => `${Math.round(width)}×${Math.round(height)} pt`,
    sourceBadge: (fileName, pageNumber) => `${fileName} · source p.${pageNumber}`,
    orientation: (portrait) => (portrait ? 'portrait' : 'landscape'),
    statusLoading: (name) => `Reading ${name}…`,
    statusLoaded: (files, pages) =>
      `Loaded ${files === 1 ? '1 file' : `${files} files`}, ${pages === 1 ? '1 page' : `${pages} pages`} in total.`,
    statusReset: () => 'Started over. Loaded files and previews were cleared.',
    statusKept: (files, pages) =>
      `The new file was not used. Your earlier work (${files === 1 ? '1 file' : `${files} files`}, ${pages === 1 ? '1 page' : `${pages} pages`}) and your selection are still here.`,
    statusExported: (name, pages) => `Started downloading ${name} (${pages === 1 ? '1 page' : `${pages} pages`}).`,
    statusIgnored: (names) => `This tool uses one file at a time, so these were left out: ${names.join(', ')}`,
    statusRangeSelected: (pages) => `${pages === 1 ? '1 page' : `${pages} pages`} selected for extraction.`,
    statusDeleted: (n) => `Deleted ${n === 1 ? '1 page' : `${n} pages`}.`,
    statusRotated: (n, degrees) =>
      `Rotated ${n === 1 ? '1 page' : `${n} pages`} 90° ${degrees > 0 ? 'right' : 'left'}.`,
    statusMoved: (from, to) => `Moved page ${from} to position ${to}.`,
    fileMeta: (pages, mib) => `${pages === 1 ? '1 page' : `${pages} pages`} · ${mib} MiB`,
    exportDisabledNoPages: () =>
      'No pages are left, so there is nothing to download. Start over to bring the pages back.',
  },

  errors: {
    notPdf: (p) => `${p.name ?? 'That file'} is not a PDF. Choose a file whose name ends in .pdf.`,
    encrypted: (p) =>
      `${p.name ?? 'That file'} is a password-protected PDF. This tool does not remove passwords. Remove the protection in the program that produced the PDF, then try again.`,
    corrupt: (p) =>
      `${p.name ?? 'That file'} could not be read. It is either damaged or uses a structure this tool cannot handle.${p.detail ? ` (Reported: ${p.detail})` : ''}`,
    noPages: (p) => `${p.name ?? 'That file'} contains no pages.`,
    emptyFile: (p) => `${p.name ?? 'That file'} is empty (0 bytes).`,
    fileTooLarge: (p) =>
      `${p.name ?? 'That file'} is over the per-file limit. Limit ${p.limitMiB} MiB, this file ${p.actualMiB} MiB.`,
    totalTooLarge: (p) =>
      `Adding ${p.name ?? 'that file'} would pass the combined size limit. Limit ${p.limitMiB} MiB, total ${p.actualMiB} MiB. Files already loaded were left untouched.`,
    tooManyPages: (p) =>
      `Adding ${p.name ?? 'that file'} would pass the page limit. Limit ${p.limitPages} pages, total ${p.actualPages} pages. Files already loaded were left untouched.`,
    tooManyFiles: (p) => `Up to ${p.limitFiles} files can be handled at once.`,
    rangeEmpty: (p) => `Type the pages to extract, for example 1,3-5 (this document has ${p.pageCount} pages).`,
    rangeSyntax: (p) => `"${p.token}" cannot be read as a page number or range. Use the form 1,3-5.`,
    rangeZero: (p) => `Page numbers start at 1, so "${p.token}" needs fixing.`,
    rangeOutOfBounds: (p) =>
      `Page ${p.page} does not exist in this document, which has ${p.pageCount} pages (check "${p.token}").`,
    rangeReversed: (p) => `"${p.token}" starts higher than it ends. Put the smaller number first.`,
    rangeDuplicate: (p) => `Page ${p.page} appears twice (in "${p.token}"). List each page once.`,
    noFiles: () => 'Choose a PDF file first.',
    noPagesLeft: () => 'No pages are left, so there was nothing to write. The export was stopped.',
    noSelection: () => 'Select at least one page first.',
    exportFailed: (p) => `The new PDF could not be built.${p.detail ? ` (Reported: ${p.detail})` : ''} Please try again.`,
    renderFailed: (p) => `The preview could not be rendered.${p.detail ? ` (Reported: ${p.detail})` : ''}`,
  },
};
